import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../api/client';
import { Layout } from '../components/Layout';

export const ReservationsSearchPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState(20);
  const [find, setFind] = useState('');
  const [findstring, setFindstring] = useState('');
  const [sdate, setSdate] = useState('');
  const [edate, setEdate] = useState('');
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [hasSearched, setHasSearched] = useState(false);
  const [cancellingEndIdx, setCancellingEndIdx] = useState<string | null>(null);

  const { data: reservationsData, isLoading, error, refetch } = useQuery({
    queryKey: ['reservationsSearch', page, rows, find, findstring, sdate, edate, hasSearched],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('rows', rows.toString());
      if (find) params.append('find', find);
      if (findstring) params.append('findstring', findstring);
      if (sdate) params.append('sdate', sdate);
      if (edate) params.append('edate', edate);

      const response = await apiClient.post<any>(`/reservations/search?${params.toString()}`);
      return response.data;
    },
    enabled: hasSearched,
  });

  const handleSearch = () => {
    setPage(1); // Reset to first page on new search
    setHasSearched(true);
  };

  const handleReset = () => {
    setPage(1);
    setRows(20);
    setFind('');
    setFindstring('');
    setSdate('');
    setEdate('');
    setHasSearched(false);
  };

  const toggleRow = (index: number) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const handleCheckReservation = (reservation: any) => {
    // gname(이름)과 hp(연락처)를 URL 파라미터로 전달
    const name = reservation.gname || '';
    const phone = reservation.hp || '';
    navigate(`/reservations/check?name=${encodeURIComponent(name)}&phone=${encodeURIComponent(phone)}`);
  };

  // 예약 취소 mutation
  const cancelMutation = useMutation({
    mutationFn: async (endIdx: string) => {
      setCancellingEndIdx(endIdx);
      const response = await apiClient.post(`/reservations/cancel-public/${endIdx}`);
      return response.data;
    },
    onSuccess: () => {
      setCancellingEndIdx(null);
      alert('예약이 성공적으로 취소되었습니다.');
      queryClient.invalidateQueries({ queryKey: ['reservationsSearch'] });
      refetch();
    },
    onError: (error: any) => {
      setCancellingEndIdx(null);
      const errorMessage = error?.response?.data?.detail || '예약 취소에 실패했습니다.';
      alert(errorMessage);
    },
  });

  const handleCancelReservation = (reservation: any) => {
    const endIdx = reservation.end_idx;

    if (!endIdx) {
      alert('예약 번호를 찾을 수 없습니다.');
      return;
    }

    if (window.confirm(`정말로 이 예약을 취소하시겠습니까?\n\n예약번호: ${endIdx}\n차량: ${reservation.carName}\n임차인: ${reservation.gname}`)) {
      cancelMutation.mutate(endIdx);
    }
  };

  const reservations = reservationsData?.rows || [];
  const totalRecords = reservationsData?.records || 0;

  return (
    <Layout>
      <div className="px-2 py-4">
        <h1 className="text-2xl font-semibold text-gray-900 mb-4">예약 조회</h1>

        {/* Search Form */}
        <div className="bg-white border border-slate-200 rounded-lg px-3 py-3 mb-4">
          <div className="flex items-end gap-3 flex-wrap">
            <div className="flex-shrink-0">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                배차일
              </label>
              <input
                type="date"
                value={sdate}
                onChange={(e) => setSdate(e.target.value)}
                className="block w-40 rounded-md border border-gray-300 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 text-sm px-3 py-2"
              />
            </div>

            <div className="flex-shrink-0">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                회차예정일
              </label>
              <input
                type="date"
                value={edate}
                onChange={(e) => setEdate(e.target.value)}
                className="block w-40 rounded-md border border-gray-300 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 text-sm px-3 py-2"
              />
            </div>

            <div className="flex-shrink-0">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                조회 컬럼
              </label>
              <select
                value={find}
                onChange={(e) => setFind(e.target.value)}
                className="block w-40 rounded-md border border-gray-300 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 text-sm px-3 py-2"
              >
                <option value="">전체</option>
                <option value="gname">임차인</option>
                <option value="carNum">차량번호</option>
                <option value="reservationDcodeName">예약담당자</option>
              </select>
            </div>

            <div className="flex-shrink-0">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                검색어
              </label>
              <input
                type="text"
                value={findstring}
                onChange={(e) => setFindstring(e.target.value)}
                className="block w-40 rounded-md border border-gray-300 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 text-sm px-3 py-2"
                placeholder="검색어 입력"
                disabled={!find}
              />
            </div>

            <div className="flex-shrink-0">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                페이지당 행 수
              </label>
              <select
                value={rows}
                onChange={(e) => setRows(Number(e.target.value))}
                className="block w-32 rounded-md border border-gray-300 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 text-sm px-3 py-2"
              >
                <option value={20}>20개</option>
                <option value={50}>50개</option>
                <option value={100}>100개</option>
              </select>
            </div>

            <button
              onClick={handleSearch}
              className="px-6 py-2 bg-indigo-600 text-white text-sm rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              조회
            </button>
            <button
              onClick={handleReset}
              className="px-4 py-2 bg-gray-200 text-gray-700 text-sm rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            >
              초기화
            </button>
          </div>
        </div>

        {/* Results */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-gray-900">
              검색 결과
              {totalRecords > 0 && <span className="ml-2 text-sm font-normal text-gray-600">({totalRecords}건)</span>}
            </h2>
          </div>

          {isLoading ? (
            <div className="bg-white border border-slate-200 rounded-lg px-6 py-12 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-slate-600"></div>
              <p className="mt-3 text-slate-600 text-sm">검색 중...</p>
            </div>
          ) : error ? (
            <div className="bg-white border border-slate-200 rounded-lg px-6 py-12 text-center">
              <p className="text-rose-600 text-sm">예약 조회에 실패했습니다.</p>
              <p className="text-sm text-gray-500 mt-2">
                {(error as any)?.response?.data?.detail || (error as Error).message}
              </p>
            </div>
          ) : !reservationsData ? (
            <div className="bg-white border border-slate-200 rounded-lg px-6 py-12 text-center">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <p className="mt-2 text-gray-500">검색 조건을 선택하고 조회 버튼을 누르세요.</p>
            </div>
          ) : reservations.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-lg px-6 py-12 text-center">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <p className="mt-2 text-gray-500">검색 결과가 없습니다.</p>
              {reservationsData && reservationsData.code !== "0" && (
                <div className="mt-4 p-4 bg-yellow-50 rounded max-w-2xl mx-auto">
                  <p className="text-sm text-yellow-800">
                    <strong>외부 API 메시지:</strong> {reservationsData.msg}
                  </p>
                  <p className="text-xs text-yellow-600 mt-1">
                    (응답 코드: {reservationsData.code})
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-2 py-2 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                      상세
                    </th>
                    <th className="px-3 py-2 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                      차량번호
                    </th>
                    <th className="px-3 py-2 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                      차종
                    </th>
                    <th className="px-3 py-2 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                      임차인
                    </th>
                    <th className="px-3 py-2 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                      연락처
                    </th>
                    <th className="px-3 py-2 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                      배차일
                    </th>
                    <th className="px-3 py-2 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                      회차예정일
                    </th>
                    <th className="px-3 py-2 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                      렌트금액
                    </th>
                    <th className="px-3 py-2 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                      예약담당자
                    </th>
                    <th className="px-3 py-2 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                      상태
                    </th>
                    <th className="px-3 py-2 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                      액션
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {reservations.map((reservation: any, index: number) => (
                    <React.Fragment key={index}>
                      <tr className="hover:bg-gray-50">
                        <td className="px-2 py-2 whitespace-nowrap text-sm">
                          <button
                            onClick={() => toggleRow(index)}
                            className="text-indigo-600 hover:text-indigo-900 focus:outline-none"
                          >
                            {expandedRows.has(index) ? '▼' : '▶'}
                          </button>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 font-medium">
                          {reservation.carNum}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                          {reservation.carName}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                          {reservation.gname}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                          {reservation.hp}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                          <div dangerouslySetInnerHTML={{ __html: reservation.startdate.replace(/오전|오후/g, '').trim() }} />
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                          <div dangerouslySetInnerHTML={{ __html: reservation.yenddate.replace(/오전|오후/g, '').trim() }} />
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                          <div dangerouslySetInnerHTML={{ __html: reservation.rentmoney }} />
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                          {reservation.reservationDcodeName}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm">
                          <span className={`px-2 py-0.5 inline-flex text-xs leading-5 font-semibold rounded ${
                            reservation.endstate === '예약'
                              ? 'bg-blue-100 text-blue-700'
                              : reservation.endstate === '배차'
                              ? 'bg-green-100 text-green-700'
                              : reservation.endstate === '예약취소'
                              ? 'bg-red-100 text-red-700'
                              : 'bg-gray-100 text-gray-700'
                          }`}>
                            {reservation.endstate || '예약'}
                          </span>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm">
                          <div className="flex gap-1">
                            <button
                              onClick={() => handleCheckReservation(reservation)}
                              className="inline-flex items-center justify-center h-8 px-2.5 rounded-md border bg-white shadow-sm hover:bg-gray-50 transition-colors"
                              title="예약확인"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-indigo-600">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                <polyline points="14 2 14 8 20 8"></polyline>
                                <line x1="16" y1="13" x2="8" y2="13"></line>
                                <line x1="16" y1="17" x2="8" y2="17"></line>
                                <polyline points="10 9 9 9 8 9"></polyline>
                              </svg>
                            </button>
                            {reservation.endstate === '예약' && reservation.end_idx && reservation.reservationDcodeName === '굿타임kr' && (
                              <button
                                onClick={() => handleCancelReservation(reservation)}
                                disabled={cancellingEndIdx === reservation.end_idx}
                                className="inline-flex items-center justify-center h-8 px-2.5 rounded-md border bg-white shadow-sm hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                title="취소"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-red-600">
                                  <circle cx="12" cy="12" r="10"></circle>
                                  <line x1="15" y1="9" x2="9" y2="15"></line>
                                  <line x1="9" y1="9" x2="15" y2="15"></line>
                                </svg>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                      {expandedRows.has(index) && (
                        <tr>
                          <td colSpan={11} className="px-4 py-2 bg-gray-50">
                            <div className="text-sm">
                              <div className="font-semibold text-gray-700 mb-2">원본 JSON 데이터:</div>
                              <pre className="bg-white p-3 rounded border border-gray-200 overflow-x-auto text-xs">
                                {JSON.stringify(reservation, null, 2)}
                              </pre>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>

              {/* Pagination */}
              <div className="bg-gray-50 px-4 py-3 flex items-center justify-between border-t border-gray-200">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    이전
                  </button>
                  <button
                    onClick={() => setPage(page + 1)}
                    disabled={reservations.length < rows}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    다음
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      페이지 <span className="font-medium">{page}</span>
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                      <button
                        onClick={() => setPage(Math.max(1, page - 1))}
                        disabled={page === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span className="sr-only">이전</span>
                        <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                          <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </button>
                      <button
                        onClick={() => setPage(page + 1)}
                        disabled={reservations.length < rows}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span className="sr-only">다음</span>
                        <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                          <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};
